import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import '../styles/Permissions.css';

function Permissions() {
  const navigate = useNavigate();
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadPermissions();
  }, []);

  const loadPermissions = async () => {
    try {
      setLoading(true);
      const response = await api.getPermissions();
      setPermissions(response.data || []);
      setError('');
    } catch (err) {
      setError('Failed to load permissions: ' + err.message);
      console.error('Error loading permissions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (permissionId) => {
    navigate(`/permissions/edit/${permissionId}`);
  };

  const handleDelete = async (permissionId) => {
    if (!window.confirm('Are you sure you want to delete this permission? This may affect roles that have this permission.')) {
      return;
    }

    try {
      await api.deletePermission(permissionId);
      setError('');
      alert('Permission deleted successfully!');
      loadPermissions();
    } catch (err) {
      setError('Failed to delete permission: ' + err.message);
    }
  };

  const groupPermissionsByResource = () => {
    const grouped = {};
    permissions.forEach(permission => {
      const resource = permission.resource || 'other';
      if (!grouped[resource]) {
        grouped[resource] = [];
      }
      grouped[resource].push(permission);
    });
    return grouped;
  };

  if (loading) {
    return <div className="permissions-container"><div className="loading">Loading...</div></div>;
  }

  const groupedPermissions = groupPermissionsByResource();

  return (
    <div className="permissions-container">
      <div className="permissions-header">
        <h1>Permission Management</h1>
        <button className="btn btn-primary" onClick={() => navigate('/permissions/new')}>
          Create New Permission
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="permissions-info">
        <p>Total Permissions: <strong>{permissions.length}</strong></p>
        <p>Resources: <strong>{Object.keys(groupedPermissions).length}</strong></p>
      </div>

      <div className="permissions-list">
        {Object.keys(groupedPermissions).length === 0 ? (
          <div className="no-permissions">
            <p>No permissions found</p>
            <button className="btn btn-primary" onClick={() => navigate('/permissions/new')}>
              Create Your First Permission
            </button>
          </div>
        ) : (
          Object.keys(groupedPermissions).sort().map(resource => (
            <div key={resource} className="resource-group">
              <div className="resource-header">
                <h2>{resource}</h2>
                <span className="permission-count">
                  {groupedPermissions[resource].length} permissions
                </span>
              </div>
              <div className="permissions-table">
                <table>
                  <thead>
                    <tr>
                      <th>Permission Name</th>
                      <th>Action</th>
                      <th>Description</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupedPermissions[resource].map(permission => (
                      <tr key={permission.id}>
                        <td className="permission-name">{permission.name}</td>
                        <td>
                          <span className="badge">{permission.action}</span>
                        </td>
                        <td className="permission-description">
                          {permission.description || '-'}
                        </td>
                        <td className="permission-actions">
                          <button
                            onClick={() => handleEdit(permission.id)}
                            className="btn btn-sm btn-secondary"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(permission.id)}
                            className="btn btn-sm btn-danger"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default Permissions;
